import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fetchLayoutFormPage } from '../services/cardLayouts';

/**
 * The page images of a page layout's form (KPR-1860), as object URLs an <img>
 * can draw: the route takes a bearer token, so a plain `src` cannot read it.
 *
 *   const { pages, state, retry } = useFormPageImages({ cardId, layoutId, form, collectionId });
 *   pages[2]   // { status: 'loading' | 'ready' | 'failed' | 'missing', url }
 *   state      // 'none' (no form) | 'loading' | 'ready' | 'failed' | 'missing'
 *
 * A page is `ready` only once the browser has DECODED it: an image still
 * decoding when the print dialog opens is a blank sheet with the record's
 * values on it. `failed` is a page worth another try; that includes a 404
 * `form_page_not_found`, which means the form was replaced under this tab and
 * reading the layout again finds the new one. `missing` is a 403 or a bare 404,
 * the same refusal a layout the reader cannot see gets, so a caller says the
 * same sentence for it.
 *
 * `collectionId` is the collection the person is printing from: a reader in a
 * sub-collection reaches a parent's layout only by naming it.
 *
 * At most CONCURRENT pages are read at a time, so a 20-page form does not spend
 * a burst of the `fileView` rate limit. Nothing is cached past this component:
 * every URL is revoked when the form changes or it unmounts.
 */
export const CONCURRENT = 3;

const isRefused = (error) => {
	const status = error?.response?.status;
	return (status === 403 || status === 404) && error?.response?.data?.code !== 'form_page_not_found';
};

/** Resolves once the browser can paint the image; rejects on one that will not decode. */
const decoded = (url) => {
	const image = new Image();
	image.src = url;
	return typeof image.decode === 'function' ? image.decode() : Promise.resolve();
};

const LOADING = Object.freeze({ status: 'loading' });
const NO_PAGES = Object.freeze({});

export default function useFormPageImages({ cardId, layoutId, form, collectionId = null }) {
	const fileId = form?.fileId || null;
	const count = form?.pages?.length || 0;
	const key = fileId ? `${layoutId}:${fileId}:${count}:${collectionId || ''}` : null;

	// `{ key, pages }`: pages read for another form are not this form's, in the
	// render before the effect below has run.
	const [loaded, setLoaded] = useState({ key: null, pages: {} });
	const [attempt, setAttempt] = useState(0);
	// The URLs this component holds, page by page.
	const held = useRef({ key: null, urls: new Map() });

	const revokeAll = useCallback(() => {
		held.current.urls.forEach(url => URL.revokeObjectURL(url));
		held.current.urls.clear();
	}, []);

	useEffect(() => revokeAll, [revokeAll]);

	useEffect(() => {
		if (held.current.key !== key) {
			revokeAll();
			held.current.key = key;
			setLoaded({ key, pages: {} });
		}
		if (!key) return undefined;
		const controller = new AbortController();
		const { signal } = controller;
		const { urls } = held.current;
		const set = (number, page) => setLoaded(last => (last.key === key ? { key, pages: { ...last.pages, [number]: page } } : last));

		// Only what is not in hand: Retry does not read a good page twice.
		const wanted = [];
		for (let number = 1; number <= count; number += 1) {
			if (urls.has(number)) continue;
			wanted.push(number);
			set(number, LOADING);
		}

		const read = async (number) => {
			let url = null;
			try {
				const blob = await fetchLayoutFormPage(cardId, layoutId, number, { file: fileId, collectionId, signal });
				url = URL.createObjectURL(blob);
				await decoded(url);
				if (signal.aborted) {
					URL.revokeObjectURL(url);
					return;
				}
				urls.set(number, url);
				set(number, { status: 'ready', url });
			} catch (error) {
				if (url) URL.revokeObjectURL(url);
				if (!signal.aborted) set(number, { status: isRefused(error) ? 'missing' : 'failed' });
			}
		};
		const worker = async () => {
			while (wanted.length) {
				if (signal.aborted) return;
				await read(wanted.shift()); // eslint-disable-line no-await-in-loop
			}
		};
		Array.from({ length: Math.min(CONCURRENT, wanted.length) }, worker);
		return () => controller.abort();
	}, [key, cardId, layoutId, fileId, count, collectionId, attempt, revokeAll]);

	const retry = useCallback(() => setAttempt(n => n + 1), []);

	return useMemo(() => {
		if (!key) return { pages: NO_PAGES, state: 'none', retry };
		const pages = loaded.key === key ? loaded.pages : NO_PAGES;
		const statuses = Array.from({ length: count }, (_, index) => pages[index + 1]?.status || 'loading');
		let state = 'ready';
		if (statuses.includes('missing')) state = 'missing';
		else if (statuses.includes('failed')) state = 'failed';
		else if (statuses.includes('loading')) state = 'loading';
		return { pages, state, retry };
	}, [key, loaded, count, retry]);
}
